import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { triggerPortalRefresh } from '@/hooks/usePortalRefresh';

const SCHWELLE = 72;

/** Pull-to-Refresh für den Portal-Scrollbereich (nur Touch-Geräte): am oberen Rand herunterziehen → Seite aktualisiert sich (usePortalRefresh) oder lädt neu. */
export const PullToRefresh = ({ scrollRef }) => {
  const [zug, setZug] = useState(0);
  const [aktiv, setAktiv] = useState(false);
  const [ziehen, setZiehen] = useState(false);
  const start = useRef(null);
  const zugRef = useRef(0);
  const aktivRef = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !window.matchMedia('(pointer: coarse)').matches) return undefined;
    const setze = (v) => { zugRef.current = v; setZug(v); };
    const onStart = (e) => {
      start.current = el.scrollTop <= 0 && !aktivRef.current ? e.touches[0].clientY : null;
    };
    const onMove = (e) => {
      if (start.current == null) return;
      const d = e.touches[0].clientY - start.current;
      if (d <= 0 || el.scrollTop > 0) { if (zugRef.current) setze(0); return; }
      setZiehen(true);
      setze(Math.min(130, d * 0.55));
      if (d > 8 && e.cancelable) e.preventDefault();
    };
    const onEnd = async () => {
      if (start.current == null) return;
      start.current = null;
      setZiehen(false);
      if (zugRef.current < SCHWELLE) { setze(0); return; }
      aktivRef.current = true;
      setAktiv(true);
      setze(SCHWELLE * 0.8);
      try { await Promise.all([triggerPortalRefresh(), new Promise(r => setTimeout(r, 600))]); }
      finally { aktivRef.current = false; setAktiv(false); setze(0); }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [scrollRef]);

  const bereit = zug >= SCHWELLE;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-center" aria-live="polite" data-testid="pull-to-refresh"
      data-state={aktiv ? 'laedt' : bereit ? 'bereit' : zug > 0 ? 'ziehen' : 'ruhe'}
      style={{ transform: `translateY(${zug - 48}px)`, opacity: zug > 6 ? 1 : 0, transition: ziehen ? 'none' : 'transform .25s ease, opacity .2s ease' }}>
      <div className={`w-10 h-10 rounded-full bg-white shadow-lg border flex items-center justify-center ${bereit || aktiv ? 'border-brand-gold' : 'border-gray-200'}`}>
        <RefreshCw className={`w-5 h-5 ${bereit || aktiv ? 'text-brand-gold' : 'text-brand-navy'} ${aktiv ? 'animate-spin' : ''}`}
          style={aktiv ? undefined : { transform: `rotate(${zug * 2.5}deg)` }} />
      </div>
    </div>
  );
};

export default PullToRefresh;
